import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close, Send } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [feedbackForm, setFeedbackForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Compose email with form data
    const subject = encodeURIComponent('Feedback - Nagrik Sahayak');
    const body = encodeURIComponent(
      `Name: ${feedbackForm.name || 'Not provided'}\n` +
      `Email: ${feedbackForm.email || 'Not provided'}\n\n` +
      `Message:\n${feedbackForm.message}`
    );
    
    // Open email client with pre-filled data
    window.location.href = `mailto:support@nagriksahayak.in?subject=${subject}&body=${body}`;
    
    // Reset form and close modal after a short delay
    setTimeout(() => {
      setFeedbackForm({ name: '', email: '', message: '' });
      onClose();
    }, 500);
  };

  const handleClose = () => {
    setFeedbackForm({ name: '', email: '', message: '' });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 6
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#673AB7', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 2
      }}>
        <Typography variant="h6" fontWeight="bold">
          {t('send_us_feedback')}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{ color: 'white' }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('feedback_description')}
          </Typography>
          
          <TextField
            fullWidth
            label={t('your_name_optional')}
            variant="outlined"
            margin="normal"
            value={feedbackForm.name}
            onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
          />
          
          <TextField
            fullWidth
            label={t('your_email_optional')}
            variant="outlined"
            margin="normal"
            type="email"
            value={feedbackForm.email}
            onChange={(e) => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
            helperText={t('email_response_note')}
          />
          
          <TextField
            fullWidth
            label={t('your_message')}
            variant="outlined"
            margin="normal"
            multiline
            rows={5}
            required
            value={feedbackForm.message}
            onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
            helperText={t('feedback_detail_note')}
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose}
            sx={{ color: 'text.secondary' }}
          >
            {t('cancel')}
          </Button>
          <Button 
            type="submit"
            variant="contained"
            startIcon={<Send />}
            sx={{ 
              bgcolor: '#673AB7',
              '&:hover': { bgcolor: '#512DA8' }
            }}
          >
            {t('send_feedback')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
